import {Component, Input} from '@angular/core';

/**
 * Branded loading state, shown while a screen fetches its data.
 * Usage: `@if (loading) { <app-loader label="Loading accounts…"/> } @else { … }`
 */
@Component({
  selector: 'app-loader',
  standalone: true,
  template: `
    <div class="ld-wrap" role="status" [attr.aria-label]="label">
      <!-- SVG + SMIL, not a CSS-rotated box: WebKit composites a CSS rotation
           around a fractionally-offset layer origin whenever the element lands
           on a half pixel, so the old border-ring visibly ORBITED on iPhones
           instead of spinning in place. SMIL rotates INSIDE the SVG raster,
           around the exact point (16,16) - the element itself never moves, so
           there is no layer origin to get wrong.

           Same 0.8s, same 32px, same colours as the border ring it replaces;
           only the rotation mechanism changed. The arc is stroked with
           currentColor, so the overlay usages repaint it by setting the
           inherited "color" property (styles.css). -->
      <svg class="ld-ring" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
        <circle cx="16" cy="16" r="13.5" fill="none" stroke="rgba(140, 140, 140, 0.25)" stroke-width="3"/>
        <!-- 22 of the 84.8px circumference drawn, the rest gapped - the same
             quarter-ring the old border-top-color produced. -->
        <circle cx="16" cy="16" r="13.5" fill="none" stroke="currentColor" stroke-width="3"
                stroke-linecap="round" stroke-dasharray="22 62.8">
          <animateTransform attributeName="transform" attributeType="XML" type="rotate"
                            from="0 16 16" to="360 16 16" dur="0.8s" repeatCount="indefinite"/>
        </circle>
      </svg>
      <span class="ld-label">{{ label }}</span>
    </div>
  `,
  styles: `
    .ld-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      padding: 110px 20px;
    }

    /* Arc uses the theme text colour (near-black in light, near-white in dark)
       so the full-page loader - which sits on the page background - stays "black"
       in the light theme and visible in dark. Overlay usages (.pm-loading /
       .page-busy) sit on a forced-white veil and pin this to a fixed dark ink in
       styles.css. Neutral translucent track reads on both surfaces.

       Set as "color", not "stroke": the arc is currentColor, which is what lets
       those overlay overrides repaint it with one inherited property. */
    .ld-ring {
      display: block;
      color: var(--text);
    }

    .ld-label {
      font-size: 13px;
      color: var(--muted);
    }
  `
})
export class LoaderComponent {
  @Input() label = 'Loading…';
}
